import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Camera, Plus, Minus, X, ScanLine, Package } from "lucide-react";

const CATEGORIES = ["spirits", "beer", "wine", "mixer", "garnish", "other"];
const UNITS = ["bottle", "can", "keg", "case", "unit"];

const EMPTY_NEW = {
  name: "",
  category: "spirits",
  unit: "bottle",
  current_stock: 1,
  par_level: 6,
  reorder_point: 2,
  unit_cost: 0,
  supplier_id: "",
};

export default function Scan() {
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [scanned, setScanned] = useState(null); // {code, matchedItem}
  const [saving, setSaving] = useState(false);
  const [delta, setDelta] = useState(1);
  const [newForm, setNewForm] = useState(EMPTY_NEW);

  const refresh = async () => {
    const [inv, sup] = await Promise.all([
      api.get("/inventory"),
      api.get("/suppliers"),
    ]);
    setItems(inv.data);
    setSuppliers(sup.data);
    return inv.data;
  };
  useEffect(() => { refresh().catch(() => {}); }, []);

  const start = async () => {
    setScanned(null);
    setScanning(true);
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      scannerRef.current = new Html5Qrcode("scan-region");
      await scannerRef.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 260, height: 160 } },
        (decoded) => onDecoded(decoded),
        () => {}
      );
    } catch (e) {
      toast.error("Camera access denied or unsupported. Use a USB scanner instead.");
      setScanning(false);
    }
  };

  const stop = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (err) {
      console.warn("Barcode scanner stop failed (safe to ignore if already stopped):", err);
    }
    scannerRef.current = null;
    setScanning(false);
  };

  const onDecoded = async (code) => {
    // Match by SKU (case-insensitive)
    const match = items.find(
      (i) => (i.sku || "").toLowerCase() === code.toLowerCase()
    );
    setScanned({ code, matchedItem: match || null, lookup: null, lookupLoading: !match });
    if (!match) {
      // Pre-fill "add new" form with sensible defaults
      setNewForm({ ...EMPTY_NEW, name: "" });
      // Fire-and-forget product lookup
      api.get(`/lookup/barcode/${encodeURIComponent(code)}`)
        .then((r) => {
          const info = r.data;
          setScanned((prev) => prev && prev.code === code
            ? { ...prev, lookup: info, lookupLoading: false } : prev);
          if (info?.found) {
            setNewForm((f) => ({
              ...f,
              name: info.name || f.name,
              category: info.category || f.category,
            }));
            toast.success(`Product identified: ${info.name}`);
          }
        })
        .catch(() => {
          setScanned((prev) => prev && prev.code === code
            ? { ...prev, lookupLoading: false } : prev);
        });
    }
    await stop();
  };

  const createFromScan = async (e) => {
    e.preventDefault();
    if (!scanned?.code) return;
    if (!newForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/inventory", {
        name: newForm.name.trim(),
        sku: scanned.code,
        category: newForm.category,
        unit: newForm.unit,
        current_stock: Number(newForm.current_stock),
        par_level: Number(newForm.par_level),
        reorder_point: Number(newForm.reorder_point),
        unit_cost: Number(newForm.unit_cost),
        supplier_id: newForm.supplier_id || null,
      });
      toast.success(`Added "${newForm.name}" to inventory`);
      const refreshed = await refresh();
      const created = refreshed.find(
        (i) => (i.sku || "").toLowerCase() === scanned.code.toLowerCase()
      );
      setScanned({ code: scanned.code, matchedItem: created || null });
      setNewForm(EMPTY_NEW);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const applyDelta = async (sign) => {
    if (!scanned?.matchedItem) return;
    const item = scanned.matchedItem;
    const change = sign * Math.abs(Number(delta) || 1);
    setSaving(true);
    try {
      await api.put(`/inventory/${item.id}`, {
        name: item.name, sku: item.sku, category: item.category,
        unit: item.unit,
        current_stock: (item.current_stock || 0) + change,
        par_level: item.par_level, reorder_point: item.reorder_point,
        unit_cost: item.unit_cost, supplier_id: item.supplier_id,
      });
      toast.success(`${sign > 0 ? "Added" : "Removed"} ${Math.abs(change)} ${item.unit}(s)`);
      const refreshed = await refresh();
      const updated = refreshed.find((i) => i.id === item.id);
      setScanned({ ...scanned, matchedItem: updated });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  // Manual code entry fallback (for USB scanners which act as keyboards)
  const [manual, setManual] = useState("");
  const submitManual = (e) => {
    e.preventDefault();
    if (!manual.trim()) return;
    onDecoded(manual.trim());
    setManual("");
  };

  return (
    <div className="space-y-6" data-testid="scan-root">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
          Barcode / SKU
        </div>
        <h1 className="font-display text-4xl font-bold">Scan &amp; Adjust</h1>
        <p className="text-slate-500 text-sm mt-1">
          Scan a bottle to instantly update stock. Works with your phone camera or any USB barcode scanner.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="surface-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-xl font-bold flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-500" /> Camera
            </div>
            {scanning ? (
              <button onClick={stop} data-testid="stop-scan" className="text-xs px-3 py-1.5 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10">
                Stop
              </button>
            ) : (
              <button onClick={start} data-testid="start-scan" className="btn-amber rounded-md px-3 py-1.5 text-xs flex items-center gap-1">
                <ScanLine className="w-3 h-3" /> Start scanning
              </button>
            )}
          </div>
          <div id="scan-region" className="rounded-md overflow-hidden bg-black" style={{ minHeight: 240 }} />
          {!scanning && (
            <p className="text-xs text-slate-500 mt-3">
              Tip: barcode value must match an item's SKU to auto-select. Set SKUs on the Inventory page.
            </p>
          )}
        </div>

        <div className="surface-card rounded-xl p-6">
          <div className="font-display text-xl font-bold mb-4">Or type/paste code</div>
          <form onSubmit={submitManual} className="flex gap-2">
            <input
              autoFocus
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder="Scan with USB reader, or type SKU"
              className="flex-1 bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm font-mono-plex outline-none focus:border-amber-500"
              data-testid="manual-code"
            />
            <button type="submit" className="btn-amber rounded-md px-4 py-2 text-sm" data-testid="submit-manual">
              Find
            </button>
          </form>
          <p className="text-xs text-slate-500 mt-3">
            USB barcode scanners just type into the focused field like a keyboard — click the box then scan.
          </p>
        </div>
      </div>

      {scanned && (
        <div className="surface-card rounded-xl p-6" data-testid="scan-result">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-plex">Scanned</div>
              <div className="font-mono-plex text-amber-400 text-lg">{scanned.code}</div>
            </div>
            <button onClick={() => setScanned(null)}><X className="w-5 h-5 text-slate-400" /></button>
          </div>

          {scanned.matchedItem ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-2xl font-bold">{scanned.matchedItem.name}</div>
                  <div className="text-xs text-slate-500 font-mono-plex capitalize mt-1">
                    {scanned.matchedItem.category} · {scanned.matchedItem.unit}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 font-mono-plex">Current stock</div>
                  <div className="font-display text-4xl font-bold text-amber-500">
                    {scanned.matchedItem.current_stock}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={delta}
                  onChange={(e) => setDelta(e.target.value)}
                  data-testid="delta-input"
                  className="w-20 bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-sm font-mono-plex outline-none focus:border-amber-500"
                />
                <button
                  onClick={() => applyDelta(1)}
                  disabled={saving}
                  data-testid="add-stock"
                  className="flex-1 rounded-md py-2.5 text-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
                <button
                  onClick={() => applyDelta(-1)}
                  disabled={saving}
                  data-testid="remove-stock"
                  className="flex-1 rounded-md py-2.5 text-sm bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <Minus className="w-4 h-4" /> Remove
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={createFromScan} className="space-y-4" data-testid="scan-new-item-form">
              <div className="flex items-center gap-2 text-amber-400 text-sm">
                <Package className="w-4 h-4" />
                No item with SKU <span className="font-mono-plex">{scanned.code}</span> — add it now.
              </div>
              {scanned.lookupLoading && (
                <div className="text-xs text-slate-500 font-mono-plex flex items-center gap-2" data-testid="lookup-loading">
                  <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                  Checking product database…
                </div>
              )}
              {scanned.lookup?.found && (
                <div className="flex gap-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3" data-testid="lookup-hit">
                  {scanned.lookup.image_url && (
                    <img
                      src={scanned.lookup.image_url}
                      alt=""
                      className="w-14 h-14 object-cover rounded"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  )}
                  <div className="flex-1 text-sm">
                    <div className="text-emerald-400 text-xs uppercase tracking-widest font-mono-plex mb-1">
                      Identified via Open Food Facts
                    </div>
                    <div className="text-slate-200">{scanned.lookup.name}</div>
                    <div className="text-xs text-slate-500 font-mono-plex mt-0.5">
                      {[scanned.lookup.brand, scanned.lookup.quantity].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </div>
              )}
              {scanned.lookup && !scanned.lookup.found && !scanned.lookupLoading && (
                <div className="text-xs text-slate-500 font-mono-plex" data-testid="lookup-miss">
                  Not in Open Food Facts — enter details manually.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-slate-400 mb-1 block">
                    Name <span className="text-amber-500">*</span>
                  </label>
                  <input
                    autoFocus
                    required
                    value={newForm.name}
                    onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                    placeholder="e.g. Woodford Reserve Bourbon 750ml"
                    className={inp}
                    data-testid="new-item-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Category</label>
                  <select
                    value={newForm.category}
                    onChange={(e) => setNewForm({ ...newForm, category: e.target.value })}
                    className={inp}
                    data-testid="new-item-category"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Unit</label>
                  <select
                    value={newForm.unit}
                    onChange={(e) => setNewForm({ ...newForm, unit: e.target.value })}
                    className={inp}
                    data-testid="new-item-unit"
                  >
                    {UNITS.map((u) => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Starting stock</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newForm.current_stock}
                    onChange={(e) => setNewForm({ ...newForm, current_stock: e.target.value })}
                    className={inp}
                    data-testid="new-item-stock"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Par level</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newForm.par_level}
                    onChange={(e) => setNewForm({ ...newForm, par_level: e.target.value })}
                    className={inp}
                    data-testid="new-item-par"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Reorder point</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newForm.reorder_point}
                    onChange={(e) => setNewForm({ ...newForm, reorder_point: e.target.value })}
                    className={inp}
                    data-testid="new-item-reorder"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Unit cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newForm.unit_cost}
                    onChange={(e) => setNewForm({ ...newForm, unit_cost: e.target.value })}
                    className={inp}
                    data-testid="new-item-cost"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-slate-400 mb-1 block">Supplier (optional)</label>
                  <select
                    value={newForm.supplier_id}
                    onChange={(e) => setNewForm({ ...newForm, supplier_id: e.target.value })}
                    className={inp}
                    data-testid="new-item-supplier"
                  >
                    <option value="">— none —</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <a
                  href="/inventory"
                  className="px-4 py-2 rounded-md border border-white/10 text-sm text-slate-300 hover:bg-white/[0.03]"
                >
                  Full form on Inventory
                </a>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-new-item"
                  className="btn-amber rounded-md px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
                >
                  <Plus className="w-4 h-4" /> {saving ? "Adding…" : "Add to inventory"}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

const inp =
  "w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none focus:border-amber-500";

